import {
  IonContent,
  IonPage,
  IonLabel,
  IonSpinner,
  IonGrid,
  IonRow,
  IonCol,
} from "@ionic/react";
import cx from "classnames";
import s from "./ShopifyApp.module.scss";
import brandLogo from "../../../Common/assets/logo/wl-woodmark-logo-rev.svg";
import { useEffect, useState, useRef } from "react";
import {
  validateDiscountPartner,
  getShopifyTransactions,
  ShopifyTransaction,
  getShopifyInstallAuthorizationUrl,
} from "../../api/shopify";
import { useQuery } from "@tanstack/react-query";
import { bugNotif } from "../../api/bugsnag";
import { useAppBridge } from "@shopify/app-bridge-react";
import createApp from "@shopify/app-bridge";
import { getSessionToken } from "@shopify/app-bridge/utilities";
import { getEnv } from "../../env";

const DebugPanel: React.FC<{
  isDebugMode: boolean;
  onExitDebugMode: () => void;
  shop: string | null;
  host: string | null;
  partnerData: any;
  isLoadingPartner: boolean;
  partnerError: any;
  transactionsData: any;
  isLoadingTransactions: boolean;
  transactionsError: any;
  shouldTriggerOauth: boolean | undefined;
  oauthStatus?: {
    status: "idle" | "fetching" | "success" | "error";
    authorizationURL?: string;
    error?: string;
  };
}> = ({
  shop,
  host,
  partnerData,
  isLoadingPartner,
  partnerError,
  transactionsData,
  isLoadingTransactions,
  transactionsError,
  shouldTriggerOauth,
  oauthStatus,
  isDebugMode,
  onExitDebugMode,
}) => {
  if (!isDebugMode) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: "rgba(0, 0, 0, 0.9)",
        color: "white",
        padding: "10px",
        fontSize: "12px",
        fontFamily: "monospace",
        zIndex: 9999,
        maxHeight: "300px",
        overflow: "auto",
        borderBottom: "2px solid #ff6b6b",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "8px",
        }}
      >
        <div style={{ fontWeight: "bold", color: "#ff6b6b" }}>
          🐛 DEBUG PANEL (Remove before production)
        </div>
        <button
          onClick={onExitDebugMode}
          style={{
            backgroundColor: "#ff6b6b",
            color: "white",
            border: "none",
            padding: "4px 12px",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "11px",
            fontWeight: "600",
          }}
        >
          Exit Debug Mode
        </button>
      </div>
      <div style={{ marginBottom: "4px" }}>
        <strong>Shop:</strong> {shop || "❌ Missing"}
      </div>
      <div style={{ marginBottom: "4px" }}>
        <strong>Host:</strong> {host || "❌ Missing"}
      </div>
      <div style={{ marginBottom: "4px" }}>
        <strong>Query Params:</strong>{" "}
        {(() => {
          const params = new URLSearchParams(window.location.search);
          const paramEntries: string[] = [];
          params.forEach((value, key) => {
            paramEntries.push(`${key}=${value}`);
          });
          return paramEntries.length > 0 ? (
            <span style={{ fontFamily: "monospace", fontSize: "11px" }}>
              {paramEntries.join(", ")}
            </span>
          ) : (
            "None"
          );
        })()}
      </div>
      <div style={{ marginBottom: "4px" }}>
        <strong>Partner Loading:</strong>{" "}
        {isLoadingPartner ? "⏳ Loading..." : "✅ Complete"}
      </div>
      <div style={{ marginBottom: "4px" }}>
        <strong>Partner Exists:</strong>{" "}
        {partnerData?.exists
          ? "✅ Yes"
          : partnerData?.exists === false
            ? "❌ No"
            : "⏳ Unknown"}
      </div>
      <div style={{ marginBottom: "4px" }}>
        <strong>Should Trigger OAuth:</strong>{" "}
        {shouldTriggerOauth ? "✅ Yes" : "❌ No"}
      </div>
      <div style={{ marginBottom: "4px" }}>
        <strong>Transactions Loading:</strong>{" "}
        {isLoadingTransactions ? "⏳ Loading..." : "✅ Complete"}
      </div>
      <div style={{ marginBottom: "4px" }}>
        <strong>Transactions Count:</strong>{" "}
        {transactionsData?.transactions?.length || 0}
      </div>
      {partnerError && (
        <div style={{ marginBottom: "4px", color: "#ff6b6b" }}>
          <strong>Partner Error:</strong> {String(partnerError)}
        </div>
      )}
      {transactionsError && (
        <div style={{ marginBottom: "4px", color: "#ff6b6b" }}>
          <strong>Transactions Error:</strong> {String(transactionsError)}
        </div>
      )}
      {shouldTriggerOauth && (
        <>
          <div
            style={{
              marginTop: "8px",
              marginBottom: "4px",
              borderTop: "1px solid #444",
              paddingTop: "8px",
            }}
          >
            <strong style={{ color: "#4dabf7" }}>OAuth Flow Status:</strong>
          </div>
          <div style={{ marginBottom: "4px", marginLeft: "10px" }}>
            <strong>Status:</strong>{" "}
            {oauthStatus?.status === "fetching"
              ? "⏳ Fetching auth URL..."
              : ""}
            {oauthStatus?.status === "success"
              ? "✅ Got auth URL, redirecting..."
              : ""}
            {oauthStatus?.status === "error"
              ? `❌ Error: ${oauthStatus.error || "Unknown"}`
              : ""}
            {!oauthStatus ? "⏳ Not started" : ""}
          </div>
          {oauthStatus?.authorizationURL && (
            <div
              style={{
                marginBottom: "4px",
                marginLeft: "10px",
                fontSize: "10px",
                wordBreak: "break-all",
              }}
            >
              <strong>Auth URL:</strong>{" "}
              {oauthStatus.authorizationURL.substring(0, 80)}...
            </div>
          )}
        </>
      )}
      <div style={{ marginTop: "8px", fontSize: "10px", opacity: 0.7 }}>
        <strong>Flow Status:</strong>{" "}
        {!shop
          ? "❌ Waiting for shop parameter"
          : isLoadingPartner
            ? "⏳ Validating partner..."
            : partnerError
              ? "❌ Partner validation failed"
              : !partnerData?.exists && shouldTriggerOauth
                ? "🚀 Triggering OAuth installation..."
                : !partnerData?.exists
                  ? "❌ Partner not installed, waiting for OAuth trigger"
                  : isLoadingTransactions
                    ? "⏳ Loading transactions..."
                    : transactionsError
                      ? "❌ Failed to load transactions"
                      : "✅ Dashboard ready"}
      </div>
    </div>
  );
};

// Component that handles OAuth flow using App Bridge v4
// This component should be rendered on the embedded App URL page
// It automatically triggers OAuth when partner doesn't exist
const EnsureOauth: React.FC<{
  onStatusChange?: (status: {
    status: "idle" | "fetching" | "success" | "error";
    authorizationURL?: string;
    error?: string;
  }) => void;
}> = ({ onStatusChange }) => {
  // In App Bridge v4, useAppBridge() returns the shopify global (not the old app instance)
  // We call it at component level as required by React hooks rules
  // Hooks must be called unconditionally - if App Bridge isn't configured, this will
  // throw and React will handle it appropriately (component won't render)
  useAppBridge();

  const ranRef = useRef(false);

  useEffect(() => {
    // Prevent running multiple times
    if (ranRef.current) return;
    ranRef.current = true;

    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const shop = params.get("shop");
      const host = params.get("host") || undefined;

      if (!shop) {
        onStatusChange?.({
          status: "error",
          error: "Missing shop parameter",
        });
        return;
      }

      onStatusChange?.({
        status: "fetching",
      });

      try {
        const response = await getShopifyInstallAuthorizationUrl(shop, host);
        const { authorizationURL } = response.data;

        if (!authorizationURL) {
          onStatusChange?.({
            status: "error",
            error: "No authorizationURL in response",
          });
          return;
        }

        onStatusChange?.({
          status: "success",
          authorizationURL,
        });

        // Escape iframe + go top-level for OAuth (App Bridge v4 compatible)
        // Using _top target escapes the iframe and navigates at the top level
        window.open(authorizationURL, "_top");
      } catch (error) {
        const errorMsg = String(error);
        bugNotif("EnsureOauth - get authorization URL error", errorMsg);
        onStatusChange?.({
          status: "error",
          error: errorMsg,
        });
      }
    };

    run();
  }, [onStatusChange]);

  return null;
};

export const ShopifyApp = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const shop = searchParams.get("shop");
  const host = searchParams.get("host");
  const [oauthStatus, setOauthStatus] = useState<{
    status: "idle" | "fetching" | "success" | "error";
    authorizationURL?: string;
    error?: string;
  }>({ status: "idle" });
  const [isDebugMode, setIsDebugMode] = useState(false);
  const debugClickCountRef = useRef(0);
  const debugClickTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debugClickTimeoutRef.current) {
        clearTimeout(debugClickTimeoutRef.current);
      }
    };
  }, []);

  const {
    data: partnerData,
    isLoading: isLoadingPartner,
    error: partnerError,
  } = useQuery({
    queryKey: ["shopify-validate", shop],
    queryFn: async () => {
      if (!shop) return null;
      const response = await validateDiscountPartner(shop);
      return response.data;
    },
    enabled: !!shop,
  });

  // Initialize App Bridge (v4 returns shopify global)
  useAppBridge();

  // Create app instance for getSessionToken (needed for session token fetching)
  // According to Shopify docs, getSessionToken requires an app instance
  // Get API key from env config (same as used in index.html meta tag)
  const shopifyApiKey = getEnv().firebase.shopifyApiKey || "";
  const app =
    shop && typeof window !== "undefined" && shopifyApiKey
      ? createApp({
          apiKey: shopifyApiKey,
          host: host || "",
        })
      : null;

  const {
    data: transactionsData,
    isLoading: isLoadingTransactions,
    error: transactionsError,
  } = useQuery({
    queryKey: ["shopify-transactions", shop],
    queryFn: async () => {
      if (!shop || !app) return null;
      // Fetch a fresh session token for each request (required by Shopify)
      // Session tokens expire after 1 minute, so we must fetch fresh ones
      // According to Shopify docs: https://shopify.dev/docs/apps/build/authentication-authorization/session-tokens
      const sessionToken = await getSessionToken(app);
      const response = await getShopifyTransactions(shop, sessionToken);
      return response.data;
    },
    enabled: !!shop && !!partnerData?.exists,
    retry: (failureCount, error) => {
      // Don't retry on 401/403 errors (authentication errors)
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as { response?: { status?: number } };
        const status = axiosError.response?.status;
        if (status === 401 || status === 403) {
          return false;
        }
      }
      return failureCount < 3;
    },
  });

  // Render EnsureOauth component if partner doesn't exist (triggers auto-install)
  const shouldTriggerOauth = !!(
    partnerData &&
    !partnerData.exists &&
    shop &&
    host
  );

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: currency || "AUD",
    }).format(amount);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("en-AU", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Handle secret debug mode toggle (5 clicks)
  const handleDebugToggleClick = () => {
    // Clear existing timeout
    if (debugClickTimeoutRef.current) {
      clearTimeout(debugClickTimeoutRef.current);
      debugClickTimeoutRef.current = null;
    }

    // Increment count using ref (synchronously, avoids stale closure)
    debugClickCountRef.current += 1;
    const newCount = debugClickCountRef.current;

    // Reset count after 2 seconds of no clicks
    debugClickTimeoutRef.current = setTimeout(() => {
      debugClickCountRef.current = 0;
    }, 2000);

    // Check if we've reached 5 clicks
    if (newCount >= 5) {
      setIsDebugMode(true);
      debugClickCountRef.current = 0;
      if (debugClickTimeoutRef.current) {
        clearTimeout(debugClickTimeoutRef.current);
        debugClickTimeoutRef.current = null;
      }
    }
  };

  const handleExitDebugMode = () => {
    setIsDebugMode(false);
    debugClickCountRef.current = 0;
    if (debugClickTimeoutRef.current) {
      clearTimeout(debugClickTimeoutRef.current);
      debugClickTimeoutRef.current = null;
    }
  };

  if (!shop) {
    return (
      <>
        <DebugPanel
          shop={shop}
          host={host}
          partnerData={partnerData}
          isLoadingPartner={isLoadingPartner}
          partnerError={partnerError}
          transactionsData={transactionsData}
          isLoadingTransactions={isLoadingTransactions}
          transactionsError={transactionsError}
          shouldTriggerOauth={shouldTriggerOauth}
          oauthStatus={oauthStatus}
          isDebugMode={isDebugMode}
          onExitDebugMode={handleExitDebugMode}
        />
        <button
          onClick={handleDebugToggleClick}
          className={s.debugToggleButton}
          aria-label="Toggle debug mode"
          type="button"
        />
        <IonPage className={s.page}>
          <IonContent className={s.content} fullscreen>
            <div className={s.body}>
              <div className={s.innerContainer}>
                <img className={s.logo} src={brandLogo} alt="" loading="eager" decoding="async" />
                <div className={s.errorContainer}>
                  <IonLabel
                    className={cx(s.errorText, "win-body medium white gilroy")}
                  >
                    Missing shop parameter. Please access this page from
                    Shopify.
                  </IonLabel>
                </div>
              </div>
            </div>
          </IonContent>
        </IonPage>
      </>
    );
  }

  if (isLoadingPartner) {
    return (
      <>
        <DebugPanel
          shop={shop}
          host={host}
          partnerData={partnerData}
          isLoadingPartner={isLoadingPartner}
          partnerError={partnerError}
          transactionsData={transactionsData}
          isLoadingTransactions={isLoadingTransactions}
          transactionsError={transactionsError}
          shouldTriggerOauth={shouldTriggerOauth}
          oauthStatus={oauthStatus}
          isDebugMode={isDebugMode}
          onExitDebugMode={handleExitDebugMode}
        />
        <button
          onClick={handleDebugToggleClick}
          className={s.debugToggleButton}
          aria-label="Toggle debug mode"
          type="button"
        />
        <IonPage className={s.page}>
          <IonContent className={s.content} fullscreen>
            <div className={s.body}>
              <div className={s.innerContainer}>
                <img className={s.logo} src={brandLogo} alt="" loading="eager" decoding="async" />
                <div className={s.loadingContainer}>
                  <IonSpinner />
                  <IonLabel
                    className={cx(
                      s.loadingText,
                      "win-body medium gilroy white"
                    )}
                  >
                    Loading...
                  </IonLabel>
                </div>
              </div>
            </div>
          </IonContent>
        </IonPage>
      </>
    );
  }

  if (partnerError) {
    bugNotif(
      "ShopifyApp - validateDiscountPartner error",
      String(partnerError)
    );
    return (
      <>
        <DebugPanel
          shop={shop}
          host={host}
          partnerData={partnerData}
          isLoadingPartner={isLoadingPartner}
          partnerError={partnerError}
          transactionsData={transactionsData}
          isLoadingTransactions={isLoadingTransactions}
          transactionsError={transactionsError}
          shouldTriggerOauth={shouldTriggerOauth}
          oauthStatus={oauthStatus}
          isDebugMode={isDebugMode}
          onExitDebugMode={handleExitDebugMode}
        />
        <button
          onClick={handleDebugToggleClick}
          className={s.debugToggleButton}
          aria-label="Toggle debug mode"
          type="button"
        />
        <IonPage className={s.page}>
          <IonContent className={s.content} fullscreen>
            <div className={s.body}>
              <div className={s.innerContainer}>
                <img className={s.logo} src={brandLogo} alt="" loading="eager" decoding="async" />
                <div className={s.errorContainer}>
                  <IonLabel
                    className={cx(s.errorText, "win-body medium gilroy")}
                  >
                    Error loading shop information. Please try again later.
                  </IonLabel>
                </div>
              </div>
            </div>
          </IonContent>
        </IonPage>
      </>
    );
  }

  // Not installed - show loading while OAuth is processing
  if (partnerData && !partnerData.exists) {
    return (
      <>
        <DebugPanel
          shop={shop}
          host={host}
          partnerData={partnerData}
          isLoadingPartner={isLoadingPartner}
          partnerError={partnerError}
          transactionsData={transactionsData}
          isLoadingTransactions={isLoadingTransactions}
          transactionsError={transactionsError}
          shouldTriggerOauth={shouldTriggerOauth}
          oauthStatus={oauthStatus}
          isDebugMode={isDebugMode}
          onExitDebugMode={handleExitDebugMode}
        />
        <button
          onClick={handleDebugToggleClick}
          className={s.debugToggleButton}
          aria-label="Toggle debug mode"
          type="button"
        />
        {shouldTriggerOauth && <EnsureOauth onStatusChange={setOauthStatus} />}
        <IonPage className={s.page}>
          <IonContent className={s.content} fullscreen>
            <div className={s.body}>
              <div className={s.innerContainer}>
                <img className={s.logo} src={brandLogo} alt="" loading="eager" decoding="async" />
                <div className={s.mainDetails}>
                  <div className={s.loadingContainer}>
                    <IonSpinner />
                    <IonLabel
                      className={cx(s.loadingText, "win-body medium gilroy")}
                    >
                      Installing Winners Locker Partner App...
                    </IonLabel>
                  </div>
                </div>
              </div>
            </div>
          </IonContent>
        </IonPage>
      </>
    );
  }

  // Installed - show dashboard
  const transactions = transactionsData?.transactions || [];
  const isLoading = isLoadingTransactions;
  const hasError = transactionsError;

  // Calculate totals
  const totalOrderAmount = transactions.reduce(
    (sum, tx) => sum + (tx.orderAmount || 0),
    0
  );
  const totalDiscountAmount = transactions.reduce(
    (sum, tx) => sum + (tx.totalDiscountAmount || 0),
    0
  );
  // Get currency from first transaction, or default to AUD
  const currency = transactions[0]?.currency || "AUD";

  return (
    <>
      <DebugPanel
        shop={shop}
        host={host}
        partnerData={partnerData}
        isLoadingPartner={isLoadingPartner}
        partnerError={partnerError}
        transactionsData={transactionsData}
        isLoadingTransactions={isLoadingTransactions}
        transactionsError={transactionsError}
        shouldTriggerOauth={shouldTriggerOauth}
        isDebugMode={isDebugMode}
        onExitDebugMode={handleExitDebugMode}
      />
      <button
        onClick={handleDebugToggleClick}
        className={s.debugToggleButton}
        aria-label="Toggle debug mode"
        type="button"
      />
      <IonPage className={s.page}>
        <IonContent className={s.content} fullscreen>
          <div className={s.body}>
            <div className={s.innerContainer}>
              <img className={s.logo} src={brandLogo} alt="" loading="eager" decoding="async" />
              <div className={s.mainDetails}>
                <div className={s.header}>
                  <IonLabel
                    className={cx(
                      s.headerTitle,
                      "win-header-4 medium winnersans primary-500"
                    )}
                  >
                    Winners Locker Dashboard
                  </IonLabel>
                  {partnerData?.partner && (
                    <IonLabel
                      className={cx(
                        s.headerDesc,
                        "win-body medium gilroy light-dark"
                      )}
                    >
                      {partnerData.partner.name}
                    </IonLabel>
                  )}
                </div>

                {isLoading ? (
                  <div className={s.loadingContainer}>
                    <IonSpinner />
                    <IonLabel
                      className={cx(s.loadingText, "win-body medium gilroy")}
                    >
                      Loading transactions...
                    </IonLabel>
                  </div>
                ) : hasError ? (
                  <div className={s.errorContainer}>
                    <IonLabel
                      className={cx(s.errorText, "win-body medium gilroy")}
                    >
                      {(() => {
                        // Check if it's an authentication error
                        if (
                          transactionsError &&
                          typeof transactionsError === "object" &&
                          "response" in transactionsError
                        ) {
                          const axiosError = transactionsError as {
                            response?: { status?: number };
                          };
                          const status = axiosError.response?.status;
                          if (status === 401 || status === 403) {
                            return "Access denied. Please ensure you're logged in to Shopify and try again.";
                          }
                        }
                        return "Error loading transactions. Please try again later.";
                      })()}
                    </IonLabel>
                  </div>
                ) : transactions.length === 0 ? (
                  <>
                    <div className={s.emptyContainer}>
                      <IonLabel
                        className={cx(s.emptyText, "win-body medium gilroy")}
                      >
                        No transactions found for this shop.
                      </IonLabel>
                    </div>
                    <div className={s.setupInfoContainer}>
                      <IonLabel
                        className={cx(
                          s.setupInfoTitle,
                          "win-sub medium gilroy"
                        )}
                      >
                        Complete Your Setup
                      </IonLabel>
                      <IonLabel
                        className={cx(
                          s.setupInfoText,
                          "win-body regular gilroy"
                        )}
                      >
                        This app is intended for approved partner merchants.
                        <br />
                        To complete setup and enable discount tracking, please
                        contact us at{" "}
                        <a
                          href="mailto:hi@winnerslocker.com.au"
                          className={s.contactLink}
                        >
                          hi@winnerslocker.com.au
                        </a>{" "}
                        to provide the discount codes you want tracked.
                      </IonLabel>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={s.statsContainer}>
                      <div className={s.statItem}>
                        <IonLabel
                          className={cx(s.statsLabel, "win-sub medium gilroy")}
                        >
                          Total Transactions: {transactions.length}
                        </IonLabel>
                      </div>
                      <div className={s.statItem}>
                        <IonLabel
                          className={cx(s.statsLabel, "win-sub medium gilroy")}
                        >
                          Total Order Amount:{" "}
                          {formatCurrency(totalOrderAmount, currency)}
                        </IonLabel>
                      </div>
                      <div className={s.statItem}>
                        <IonLabel
                          className={cx(s.statsLabel, "win-sub medium gilroy")}
                        >
                          Total Discount:{" "}
                          {formatCurrency(totalDiscountAmount, currency)}
                        </IonLabel>
                      </div>
                    </div>
                    <div className={s.transactionsContainer}>
                      <IonGrid className={s.transactionsGrid}>
                        <IonRow className={s.transactionsHeader}>
                          <IonCol size="3">
                            <IonLabel
                              className={cx(
                                s.tableHeader,
                                "win-sub medium gilroy"
                              )}
                            >
                              Order ID
                            </IonLabel>
                          </IonCol>
                          <IonCol size="2">
                            <IonLabel
                              className={cx(
                                s.tableHeader,
                                "win-sub medium gilroy"
                              )}
                            >
                              Discount Code
                            </IonLabel>
                          </IonCol>
                          <IonCol size="2">
                            <IonLabel
                              className={cx(
                                s.tableHeader,
                                "win-sub medium gilroy"
                              )}
                            >
                              Order Amount
                            </IonLabel>
                          </IonCol>
                          <IonCol size="2">
                            <IonLabel
                              className={cx(
                                s.tableHeader,
                                "win-sub medium gilroy"
                              )}
                            >
                              Discount Amount
                            </IonLabel>
                          </IonCol>
                          <IonCol size="3">
                            <IonLabel
                              className={cx(
                                s.tableHeader,
                                "win-sub medium gilroy"
                              )}
                            >
                              Date
                            </IonLabel>
                          </IonCol>
                        </IonRow>
                        {transactions.map((tx: ShopifyTransaction) => (
                          <IonRow key={tx.docId} className={s.transactionRow}>
                            <IonCol size="3">
                              <IonLabel
                                className={cx(
                                  s.tableCell,
                                  "win-body regular gilroy"
                                )}
                              >
                                #{tx.shopifyOrderId}
                              </IonLabel>
                            </IonCol>
                            <IonCol size="2">
                              <IonLabel
                                className={cx(
                                  s.tableCell,
                                  "win-body regular gilroy"
                                )}
                              >
                                {tx.discountCode}
                              </IonLabel>
                            </IonCol>
                            <IonCol size="2">
                              <IonLabel
                                className={cx(
                                  s.tableCell,
                                  "win-body regular gilroy"
                                )}
                              >
                                {formatCurrency(tx.orderAmount, tx.currency)}
                              </IonLabel>
                            </IonCol>
                            <IonCol size="2">
                              <IonLabel
                                className={cx(
                                  s.tableCell,
                                  s.discountAmount,
                                  "win-body regular gilroy"
                                )}
                              >
                                {formatCurrency(
                                  tx.totalDiscountAmount,
                                  tx.currency
                                )}
                              </IonLabel>
                            </IonCol>
                            <IonCol size="3">
                              <IonLabel
                                className={cx(
                                  s.tableCell,
                                  "win-body regular gilroy"
                                )}
                              >
                                {formatDate(tx.createdDt)}
                              </IonLabel>
                            </IonCol>
                          </IonRow>
                        ))}
                      </IonGrid>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </IonContent>
      </IonPage>
    </>
  );
};
